import React, {FC, ReactElement} from 'react';
import styled from 'styled-components';
import Combo from '../combo/Combo';
import './Slide.scss';

export type SlideProps = {
    index: number | null,
    slideActiveIndex: number | null,
    maxWidth: number,
    thumbnail: boolean,
    path: {
        slideIndex: number,
        comboIndex: number,
        setIndex: number | null,
        groupIndex: number | null,
        elementIndex: number | null,
        subElementIndex: number|null
    } | null,
    editorPlugins: any | null,
    combos: any,
    defaultBackgroundColor: string,
    defaultBackgroundColor2: string,
    settings: {
        isHidden: boolean,
        analytics: string,
        verticalAlignment: string,
        horizontalAlignment: string,
        minHeight: string, //auto|small|medium|fullScreen
        isCustomHeight: boolean, //auto|small|medium|fullScreen
        isCustomWidth: boolean,
        backgroundType: string,
        backgroundColor: string,
        overlayOpacity: string,
        gradient: string,
        backgroundImage: string,
        backgroundVideo: string,
        anchorName:string,
        isAnchorActive:boolean
    },
}

type SlideElementProps = {
    index: number | null,
    verticalAlignment: string,
    horizontalAlignment: string,
    minHeight: string,
    isCustomWidth: boolean,
    isCustomHeight: boolean,
    background: {
        backgroundColor: string,
        defaultBackgroundColor: string,
        defaultBackgroundColor2: string,
        backgroundImage: string,
        overlayOpacity: string,
        gradient: string,
    },
    isDraggingOver: boolean,
    isDragging: boolean,
    activeType?: string,
    thumbnail: boolean
}

export const SlideElement = styled.div<SlideElementProps>`
&.slide {
  justify-content: ${props => props.verticalAlignment || 'inherit'}; 
  align-items: ${props => props.horizontalAlignment || 'inherit'};
  ${props => props.minHeight === 'narrow' && !props.isCustomHeight && `
    padding: 0 16px;
  `};   
  .slideBackground {
    &:before {
      background: ${props => props.background.defaultBackgroundColor || '#ffffff'};
        ${props => props.index && (props.index + 1) % 2 === 0 && `
        background: ${props.background.defaultBackgroundColor2};
        `};
        ${props => props.background.backgroundColor && `
           background: ${props.background.backgroundColor};
        `};
        ${props => props.background.overlayOpacity === '100' && `
         opacity: 1; 
        `};
         ${props => props.background.overlayOpacity === '0' && `
         opacity: 0; 
        `};
        ${props => props.background.overlayOpacity !== '0' && props.background.overlayOpacity !== '100' && `
         opacity: ${(Number(props.background.overlayOpacity) < 10) ? '0.0' + props.background.overlayOpacity : '0.' + props.background.overlayOpacity}; 
        `};
    }
  }
  .inner-wrapper {
    max-width: 100%;
    ${props => (props.minHeight === 'auto' || props.minHeight === 'narrow') && !props.isCustomHeight && `
        min-height: 0; 
    `}; 
    ${props => props.minHeight === 'screenFit' && !props.isCustomHeight && `
        min-height: calc(100vh - 56px); 
    `};
    ${props => props.minHeight === 'screenFit' && !props.isCustomHeight && props.thumbnail && `
        min-height: 1000px; 
    `};
    ${props => props.isCustomHeight && `
         min-height: ${props.minHeight}px; 
    `};
   }
}
`;

const Slide: FC<SlideProps> = ({...props}): ReactElement => {

    const slideBackground = {
        backgroundColor: props.settings.backgroundColor,
        defaultBackgroundColor: props.defaultBackgroundColor,
        defaultBackgroundColor2: props.defaultBackgroundColor2,
        backgroundImage: props.settings.backgroundImage,
        overlayOpacity: props.settings.overlayOpacity,
        gradient: props.settings.gradient,
    };

    function getComboData(combo: any) {
        let comboData;
        if (combo.name === 'VTimeline') {
            comboData = {...combo};
            let comboSettings;
            let comboExtraFields = {};

            comboExtraFields = {
                comboDotsColor: combo.settings.comboDotsColor,
                comboLineColor: combo.settings.comboLineColor
            };
            comboSettings = {
                ...combo.settings,
                ...comboExtraFields
            };
            comboData.settings = comboSettings;
        }
        return comboData ? comboData : combo;
    }

    let isEditor = props.editorPlugins;

    if (isEditor) {
        //editor Element
        const SlideEditor = require('./SlideEditor').default;
        return <SlideEditor {...props} background={slideBackground} slideSettings={settings} editorPlugins={props.editorPlugins}/>;
    } else {
        //front Element
        return (<SlideElement className={'slide'} verticalAlignment={props.settings.verticalAlignment} horizontalAlignment={props.settings.horizontalAlignment}
            isCustomHeight={props.settings.isCustomHeight} minHeight={props.settings.minHeight} thumbnail={props.thumbnail} isCustomWidth={props.settings.isCustomWidth}
                                background={slideBackground} index={props.index} isDraggingOver={false} isDragging={false}
                              data-anchor-target={props.settings.isAnchorActive ?  props.settings.anchorName : ''}>
                <div className="slideBackground">
                    {(props.settings.backgroundType && props.settings.backgroundType.toLowerCase() === 'image') && props.settings.backgroundImage && <img src={props.settings.backgroundImage} alt="Slide Background"/>}
                    {(props.settings.backgroundType && props.settings.backgroundType.toLowerCase() === 'video') && props.settings.backgroundVideo && <video autoPlay loop muted key={props.settings.backgroundVideo}>
                        <source src={props.settings.backgroundVideo ? props.settings.backgroundVideo : ''} type="video/mp4"/>
                    </video>}
                </div>
                <div className="inner-wrapper">
                    {
                        props.combos.map((combo: any, index: number) => {
                            let comboData = getComboData(combo);
                            return <Combo key={index} comboData={comboData} editorPlugins={null} index={null} path={null} isEmptySlide={false}/>;
                        })
                    }
                </div>
            </SlideElement>
        );
    }

};

export default Slide;

export let settings: any;
const environmentLink: any = getEnvironmentLink();
let SettingsData;
try {
    SettingsData = require('../../settings-data').default;
    // do stuff
} catch (ex) {
    SettingsData = null;
}
if (SettingsData) {
    const settingsClass = new SettingsData();
    const allSettings = settingsClass.getAllSettings();
    settings = [
        [//group
            {
                settingName: 'HorizontalAlignment',
            },
            {
                settingName: 'VerticalAlignment',
            },
        ],
        [//group
            {
                settingName: 'BackgroundType',
            },
            {
                settingName: 'BackgroundColor',
                title: 'Background overlay color',
                controls: [
                    {
                        controlName: 'CustomColors',
                        property: 'backgroundColor',
                        title: '',
                        defaultValue: '#000',
                        autoOpenWhen: {
                            property: 'backgroundType',
                            value: 'backgroundColor'
                        }
                    },
                    {
                        controlName: 'Slider',
                        title: 'Overlay opacity',
                        property: 'overlayOpacity',
                        defaultValue: 80,
                        suffix: '%',
                        min: 0,
                        padding: '16px'
                    },
                ],
            }
        ],
        [
            {
                settingName: 'ElementHeight',
                controls: [
                    {
                        controlName: 'ImageSelect',
                        property: 'minHeight',
                        title: '',
                        defaultValue: 'fullScreen',
                        presetData: [
                            {
                                title: 'Auto',
                                icon: environmentLink + '/assets/images/AutoFit.svg',
                                value: 'auto',
                                width: 33
                            },
                            {
                                title: 'Screen Fit',
                                icon: environmentLink + '/assets/images/screenFit.svg',
                                value: 'screenFit',
                                width: 33
                            },
                            {
                                title: 'Narrow',
                                icon: environmentLink + '/assets/images/NarrowScreen.svg',
                                value: 'narrow',
                                width: 33
                            }
                        ]
                    },
                    {
                        controlName: 'Toggle',
                        property: 'isCustomHeight',
                        title: 'Custom',
                        defaultValue: false,
                    },
                    {
                        controlName: 'Slider',
                        title: '',
                        property: 'minHeight',
                        defaultValue: '820',
                        useDefaultValue: true,
                        suffix: 'px',
                        min: 0,
                        max: 2000,
                        step: 5,
                        condition: {
                            property: 'isCustomHeight',
                            value: true
                        }
                    },
                ],
            },
        ],
        [
            {
                settingName: 'Anchor',
            }
        ]
    ];
}

function getEnvironmentLink() {
    if (process.env.PUBLIC_URL.length) return process.env.PUBLIC_URL;
    if (process.env.REACT_APP_ENV === 'staging') return 'https://editor-bundle-staging.storydoc.com'; //staging link
    if (process.env.REACT_APP_ENV === 'production') return 'https://editor-bundle.storydoc.com'; //production link
    if (process.env.REACT_APP_ENV === 'dev') return 'http://localhost:3000'; //production link
}


